import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    useProducts,
    useProductsModerationPendingCount,
    useSetProductApproval,
    EditProductModal,
    AddToSetModal,
    RejectProductModal,
    productsApi,
    type ProductFilters as ProductFiltersType,
    type ProductCardResponse,
    productThumbSrc,
} from '@modules/products';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import { Button, Input, Pagination, QuietSelect, Tooltip } from '@shared/ui';
import { useDebounce } from '@shared/hooks';
import { ActionIcon, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IoCheckmark,
    IoClose,
    IoCloseCircleOutline,
    IoEyeOffOutline,
    IoFlameOutline,
    IoPricetagOutline,
    IoRocketOutline,
    IoSearchOutline,
    IoSparklesOutline,
    IoStar,
    IoTimeOutline,
} from 'react-icons/io5';
import { formatDate } from '@shared/utils';
import ProductsFiltersPopover, {
    productsFiltersSelectedCount,
    readStoredProductsFilters,
    writeStoredProductsFilters,
    type ProductsPageFilters,
} from './ProductsFiltersPopover';
import '@shared/styles/page.css';
import './ProductsPage.css';

type ModerationFilter = 'all' | 'pending' | 'approved' | 'rejected';

const SUPER_ADMIN_PAGE_SIZE = 25;

function getModerationLabel(product: ProductCardResponse): string {
    if (product.sellerId == null) return '—';
    if (product.approved === null) return 'Новый';
    if (product.approved) return 'Подтверждён';
    return 'Отклонён';
}

function ProductStatusIcon({ product }: { product: ProductCardResponse }) {
    if (product.sellerId == null) {
        return <span className="products-page-status-noseller">—</span>;
    }
    const label = getModerationLabel(product);
    const className =
        product.approved === true
            ? 'products-page-status-approved'
            : product.approved === false
                ? 'products-page-status-rejected'
                : 'products-page-status-pending';
    const icon =
        product.approved === true ? (
            <IoCheckmark size={18} />
        ) : product.approved === false ? (
            <IoClose size={18} />
        ) : (
            <IoTimeOutline size={18} />
        );
    const tip =
        product.approved === false && product.moderationRejectionReason
            ? product.moderationRejectionReason
            : label;
    return (
        <Tooltip label={tip} multiline maw={320}>
            <span className={`products-page-status-icon ${className}`} aria-label={label}>
                {icon}
            </span>
        </Tooltip>
    );
}

function ProductTableBadges({ product }: { product: ProductCardResponse }) {
    const hasDiscount = Boolean(product.discount && product.discountedPrice);
    const items: { key: string; label: string; className: string; icon: ReactNode }[] = [];
    if (product.promotionEnabled) {
        items.push({
            key: 'promo',
            label: 'Продвижение',
            className: 'products-page-badge--promo',
            icon: <IoRocketOutline size={16} aria-hidden />,
        });
    }
    if (hasDiscount) {
        items.push({
            key: 'sale',
            label: 'Акция',
            className: 'products-page-badge--sale',
            icon: <IoPricetagOutline size={16} aria-hidden />,
        });
    }
    if (product.isPopular) {
        items.push({
            key: 'popular',
            label: 'Популярное',
            className: 'products-page-badge--popular',
            icon: <IoFlameOutline size={16} aria-hidden />,
        });
    }
    if (product.isNew) {
        items.push({
            key: 'new',
            label: 'Новинка',
            className: 'products-page-badge--new',
            icon: <IoSparklesOutline size={16} aria-hidden />,
        });
    }
    if (product.isHidden) {
        items.push({
            key: 'hidden',
            label: 'Скрыто',
            className: 'products-page-badge--hidden',
            icon: <IoEyeOffOutline size={16} aria-hidden />,
        });
    }
    if (product.isOutOfStock) {
        items.push({
            key: 'oos',
            label: 'Нет в наличии',
            className: 'products-page-badge--out',
            icon: <IoCloseCircleOutline size={16} aria-hidden />,
        });
    }
    if (items.length === 0) return '—';
    return (
        <span className="products-page-badges">
            {items.map((item) => (
                <Tooltip key={item.key} label={item.label}>
                    <span className={`products-page-badge ${item.className}`} aria-label={item.label}>
                        {item.icon}
                    </span>
                </Tooltip>
            ))}
        </span>
    );
}

export default function ProductsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
    const [editingProductId, setEditingProductId] = useState<number | null>(null);
    const [filterValues, setFilterValues] = useState<ProductsPageFilters>(readStoredProductsFilters);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<string>('purchase-count');
    const [moderationFilter, setModerationFilter] = useState<ModerationFilter>('all');
    const [superAdminProductsPage, setSuperAdminProductsPage] = useState(1);
    const [rejectTarget, setRejectTarget] = useState<ProductCardResponse | null>(null);
    const [isPopularCronLoading, setIsPopularCronLoading] = useState(false);
    const [isHideOosCronLoading, setIsHideOosCronLoading] = useState(false);
    const [isAddToSetModalOpen, setIsAddToSetModalOpen] = useState(false);
    const [addToSetProductIds, setAddToSetProductIds] = useState<number[]>([]);

    const adminRole = useMemo(() => {
        try {
            const raw = localStorage.getItem('luxus_admin');
            if (!raw) return null;
            return (JSON.parse(raw) as { role?: string }).role ?? null;
        } catch {
            return null;
        }
    }, [location.pathname]);

    const isSuperAdmin = adminRole === 'SUPER_ADMIN';

    /** Как в Layout: счётчик модерации не запрашиваем у SELLER_ADMIN */
    const moderationPendingQueryEnabled = adminRole !== 'SELLER_ADMIN';
    const { data: moderationPendingData, isFetched: moderationPendingFetched } =
        useProductsModerationPendingCount(moderationPendingQueryEnabled);
    const moderationDefaultAppliedRef = useRef(false);

    useEffect(() => {
        if (moderationDefaultAppliedRef.current) return;
        if (!moderationPendingQueryEnabled) {
            moderationDefaultAppliedRef.current = true;
            return;
        }
        if (!moderationPendingFetched) return;
        moderationDefaultAppliedRef.current = true;
        setModerationFilter((moderationPendingData?.count ?? 0) > 0 ? 'pending' : 'all');
    }, [moderationPendingQueryEnabled, moderationPendingFetched, moderationPendingData?.count]);

    /** У SELLER_ADMIN список уже ограничен JWT; параметр sellerId на бэкенде не применяется */
    const canPickSellerFilter = adminRole !== 'SELLER_ADMIN';

    useEffect(() => {
        writeStoredProductsFilters(filterValues);
    }, [filterValues]);

    const debouncedSearchQuery = useDebounce(searchQuery, 600);
    const debouncedMinPrice = useDebounce(filterValues.minPrice, 300);
    const debouncedMaxPrice = useDebounce(filterValues.maxPrice, 300);

    const listFilters: ProductFiltersType = useMemo(() => {
        const result: ProductFiltersType = {};
        if (filterValues.colorIds.length > 0) result.colorIds = filterValues.colorIds;
        if (filterValues.flowerTypeIds.length > 0) result.flowerTypeIds = filterValues.flowerTypeIds;
        if (filterValues.assemblyTypeIds.length > 0) result.assemblyTypeIds = filterValues.assemblyTypeIds;
        if (debouncedMinPrice.trim()) {
            const min = parseFloat(debouncedMinPrice);
            if (!isNaN(min)) result.minPrice = min;
        }
        if (debouncedMaxPrice.trim()) {
            const max = parseFloat(debouncedMaxPrice);
            if (!isNaN(max)) result.maxPrice = max;
        }
        if (canPickSellerFilter && filterValues.sellerId?.trim()) {
            const sid = parseInt(filterValues.sellerId, 10);
            if (!isNaN(sid)) result.sellerId = sid;
        }
        if (filterValues.categoryId?.trim()) {
            const cid = parseInt(filterValues.categoryId, 10);
            if (!isNaN(cid)) result.categoryId = cid;
        }
        if (filterValues.ratings.length > 0) result.ratings = filterValues.ratings;
        if (filterValues.badges.length > 0) result.badges = filterValues.badges;
        result.sortBy = sortBy;
        if (debouncedSearchQuery.trim()) {
            result.search = debouncedSearchQuery.trim();
        }
        if (moderationFilter !== 'all') {
            result.moderation = moderationFilter;
        }
        return result;
    }, [
        filterValues,
        debouncedMinPrice,
        debouncedMaxPrice,
        sortBy,
        canPickSellerFilter,
        debouncedSearchQuery,
        moderationFilter,
    ]);

    const filters: ProductFiltersType = useMemo(() => {
        if (!isSuperAdmin) return listFilters;
        return {
            ...listFilters,
            page: superAdminProductsPage,
            limit: SUPER_ADMIN_PAGE_SIZE,
        };
    }, [listFilters, isSuperAdmin, superAdminProductsPage]);

    const {
        data: products = [],
        total: productsTotal = 0,
        isLoading,
        error,
        refetch,
        isFetching,
    } = useProducts(filters);
    const setApproval = useSetProductApproval();

    useEffect(() => {
        if (!isSuperAdmin) return;
        setSuperAdminProductsPage(1);
    }, [isSuperAdmin, listFilters]);

    const superAdminTotalPages = useMemo(
        () => Math.max(1, Math.ceil(productsTotal / SUPER_ADMIN_PAGE_SIZE)),
        [productsTotal],
    );

    useEffect(() => {
        if (!isSuperAdmin) return;
        if (isLoading || isFetching) return;
        if (productsTotal <= 0) return;
        if (superAdminProductsPage <= superAdminTotalPages) return;
        setSuperAdminProductsPage(superAdminTotalPages);
    }, [
        isSuperAdmin,
        isLoading,
        isFetching,
        productsTotal,
        superAdminProductsPage,
        superAdminTotalPages,
    ]);

    const handleEditProduct = (productId: number) => {
        setEditingProductId(productId);
        setIsEditProductModalOpen(true);
    };

    const handleEditProductSuccess = () => {
        setEditingProductId(null);
    };

    const handleRunPopularByPurchaseCron = async () => {
        setIsPopularCronLoading(true);
        try {
            const result = await productsApi.runSyncPopularByPurchaseCron();
            await refetch();
            notifications.show({
                title: 'Готово',
                message: `Крон выполнен. Флаг «популярный» выставлен у ${result.markedPopular} товар(ов).`,
                color: 'green',
            });
        } catch (error) {
            const msg =
                error && typeof error === 'object' && 'response' in error
                    ? (error as { response?: { data?: { message?: string } } }).response?.data
                          ?.message
                    : undefined;
            notifications.show({
                title: 'Ошибка',
                message: msg ?? 'Не удалось выполнить крон (нужны права супер-админа)',
                color: 'red',
            });
        } finally {
            setIsPopularCronLoading(false);
        }
    };

    const handleRunHideLongOutOfStockCron = async () => {
        setIsHideOosCronLoading(true);
        try {
            const result = await productsApi.runHideLongOutOfStockCron();
            await refetch();
            notifications.show({
                title: 'Готово',
                message:
                    result.hidden > 0
                        ? `Скрыто товаров не в наличии: ${result.hidden}`
                        : 'Крон выполнен: нет товаров для скрытия',
                color: 'green',
            });
        } catch (error) {
            const msg =
                error && typeof error === 'object' && 'response' in error
                    ? (error as { response?: { data?: { message?: string } } }).response?.data
                          ?.message
                    : undefined;
            notifications.show({
                title: 'Ошибка',
                message: msg ?? 'Не удалось выполнить крон (нужны права супер-админа)',
                color: 'red',
            });
        } finally {
            setIsHideOosCronLoading(false);
        }
    };

    const sortOptions = [
        { value: 'purchase-count', label: 'По количеству покупок' },
        { value: 'rating-desc', label: 'Сначала выше рейтинг' },
        { value: 'rating-asc', label: 'Сначала ниже рейтинг' },
        { value: 'price-asc', label: 'Сначала дешевле' },
        { value: 'price-desc', label: 'Сначала дороже' },
        { value: 'new', label: 'Сначала новые' },
    ];

    const moderationOptions = [
        { value: 'all', label: 'Все товары' },
        { value: 'pending', label: 'На модерации' },
        { value: 'approved', label: 'Подтверждённые' },
        { value: 'rejected', label: 'Отклонённые' },
    ];

    const productColumns: OptionsTableColumn<ProductCardResponse>[] = [
        {
            key: 'image',
            label: '',
            style: { width: 56 },
            render: (product) => {
                const url = productThumbSrc(product.images?.[0]);
                return url ? (
                    <img src={url} alt="" className="products-page-thumb" />
                ) : (
                    <span className="products-page-thumb products-page-thumb--empty">—</span>
                );
            },
        },
        {
            key: 'id',
            label: 'ID',
            style: { width: 64 },
        },
        {
            key: 'approved',
            label: 'Статус',
            render: (product) => {
                const needsModeration = product.sellerId != null && product.approved === null;
                if (needsModeration) {
                    return (
                        <span className="products-page-status-with-actions">
                            <ProductStatusIcon product={product} />
                            <span className="products-page-actions">
                                <Tooltip label="Принять">
                                    <ActionIcon
                                        variant="light"
                                        color="green"
                                        size="sm"
                                        radius="md"
                                        aria-label="Принять"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setApproval.mutate({ id: product.id, approved: true });
                                        }}
                                    >
                                        <IoCheckmark size={18} />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Отклонить">
                                    <ActionIcon
                                        variant="light"
                                        color="red"
                                        size="sm"
                                        radius="md"
                                        aria-label="Отклонить"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setRejectTarget(product);
                                        }}
                                    >
                                        <IoClose size={18} />
                                    </ActionIcon>
                                </Tooltip>
                            </span>
                        </span>
                    );
                }
                return <ProductStatusIcon product={product} />;
            },
        },
        {
            key: 'name',
            label: 'Название',
            render: (product) => (
                <span className="products-page-name-cell" title={product.name}>
                    {product.name}
                </span>
            ),
        },
        {
            key: 'badges',
            label: 'Бейджи',
            render: (product) => <ProductTableBadges product={product} />,
        },
        {
            key: 'rating',
            label: 'Рейтинг',
            style: { width: 90 },
            render: (product) =>
                product.rating != null ? (
                    <span className="products-page-rating">
                        {Number(product.rating).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}
                        <IoStar size={14} className="products-page-rating-star" aria-hidden />
                    </span>
                ) : (
                    '—'
                ),
        },
        {
            key: 'purchaseCount',
            label: 'Покупок',
            style: { width: 110 },
            render: (product) =>
                typeof product.purchaseCount === 'number'
                    ? product.purchaseCount.toLocaleString('ru-RU')
                    : '0',
        },
        {
            key: 'seller',
            label: 'Магазин',
            render: (product) => product.seller?.shopName ?? '—',
        },
        {
            key: 'price',
            label: 'Цена',
            render: (product) => {
                const p =
                    product.discountedPrice != null && product.discountedPrice !== undefined
                        ? Number(product.discountedPrice)
                        : Number(product.price);
                return `${Number.isNaN(p) ? '—' : p.toLocaleString('ru-RU')} ₽`;
            },
        },
        {
            key: 'createdAt',
            label: 'Создан',
            render: (product) => (product.createdAt ? formatDate(product.createdAt) : '—'),
        },
    ];

    return (
        <div className="page-container products-page">
            <Breadcrumbs
                items={[
                    { label: 'Главная', path: '/' },
                    { label: 'Товары' },
                ]}
            />

            <div className="page-header">
                <div
                    className="page-header-top products-page-header-top"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                    }}
                >
                    <h1>Товары</h1>
                    {isSuperAdmin && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Button
                                size="xs"
                                variant="secondary"
                                onClick={handleRunPopularByPurchaseCron}
                                disabled={isPopularCronLoading || isHideOosCronLoading}
                                loading={isPopularCronLoading}
                            >
                                Тест: cron популярные
                            </Button>
                            <Button
                                size="xs"
                                variant="secondary"
                                onClick={handleRunHideLongOutOfStockCron}
                                disabled={isPopularCronLoading || isHideOosCronLoading}
                                loading={isHideOosCronLoading}
                            >
                                Тест: cron скрыть OOS &gt; 3 дней
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="filters-section products-page-filters">
                <div className="products-page__search">
                    <Input
                        className="products-page__search-input"
                        type="text"
                        placeholder="Поиск по названию или ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        leftAddon={<IoSearchOutline size={16} aria-hidden />}
                    />
                </div>

                <QuietSelect<ModerationFilter>
                    className="products-page__select-wrap"
                    ariaLabel="Модерация товаров"
                    label={moderationOptions.find((option) => option.value === moderationFilter)?.label ?? 'Все товары'}
                    value={moderationFilter}
                    onChange={setModerationFilter}
                    options={moderationOptions.map((option) => ({
                        value: option.value as ModerationFilter,
                        label: option.label,
                    }))}
                />

                <QuietSelect<string>
                    className="products-page__select-wrap"
                    ariaLabel="Сортировка товаров"
                    label={sortOptions.find((option) => option.value === sortBy)?.label ?? 'Сортировка'}
                    value={sortBy}
                    onChange={setSortBy}
                    options={sortOptions}
                />

                <ProductsFiltersPopover
                    value={filterValues}
                    selectedCount={productsFiltersSelectedCount(filterValues)}
                    canPickSeller={canPickSellerFilter}
                    onChange={setFilterValues}
                />
            </div>

            <div className="content-wrapper products-page-content-wrapper">
                {setApproval.isPending && (
                    <div className="products-page-approval-overlay" aria-hidden>
                        <Loader size="lg" />
                    </div>
                )}
                <div className="products-table-scroll">
                    <OptionsTable
                        columns={productColumns}
                        data={products}
                        loading={isLoading || isFetching}
                        error={error}
                        refetch={refetch}
                        emptyMessage={searchQuery.trim() ? 'Ничего не найдено' : 'Нет товаров'}
                        onRowClick={(product) =>
                            navigate(`/products/${product.id}`, { state: { from: '/products' } })
                        }
                        getActions={(product) =>
                            isSuperAdmin
                                ? [
                                      {
                                          label: 'Добавить в подборку',
                                          onClick: () => {
                                              setAddToSetProductIds([product.id]);
                                              setIsAddToSetModalOpen(true);
                                          },
                                      },
                                      ...(product.sellerId != null && product.approved === true
                                          ? [
                                                {
                                                    label: 'Отклонить модерацию',
                                                    isDanger: true,
                                                    onClick: () => {
                                                        setRejectTarget(product);
                                                    },
                                                },
                                            ]
                                          : []),
                                  ]
                                : [
                                      {
                                          label: 'Редактировать',
                                          onClick: () => handleEditProduct(product.id),
                                      },
                                  ]
                        }
                    />
                </div>
                {isSuperAdmin && (
                    <Pagination
                        page={superAdminProductsPage}
                        onChange={setSuperAdminProductsPage}
                        totalItems={productsTotal}
                        pageSize={SUPER_ADMIN_PAGE_SIZE}
                    />
                )}
            </div>

            {!isSuperAdmin && editingProductId && (
                <EditProductModal
                    isOpen={isEditProductModalOpen}
                    onClose={() => {
                        setIsEditProductModalOpen(false);
                        setEditingProductId(null);
                    }}
                    productId={editingProductId}
                    onSuccess={handleEditProductSuccess}
                />
            )}

            <AddToSetModal
                isOpen={isAddToSetModalOpen}
                onClose={() => {
                    setIsAddToSetModalOpen(false);
                    setAddToSetProductIds([]);
                }}
                productIds={addToSetProductIds}
                onSuccess={() => refetch()}
            />

            <RejectProductModal product={rejectTarget} onClose={() => setRejectTarget(null)} />
        </div>
    );
}
